import { GQLCreateChargeSettingPayload, GQLUpdateChargeSettingPayload } from 'graphql/charge-settings/mutations';
import { ChargeSetting } from 'graphql/charge-settings/queries';
import { useGetListings } from 'hooks/useGetListings';
import { useFieldArray, useForm } from 'react-hook-form';
import { defaultForAll, getSettingName } from 'views/charge-settings/utils';
import { SettingForm } from './types';

const createSettingInitialValues = (prevSettingData?: ChargeSetting): SettingForm => {
  const formData: SettingForm = {
    amount_override: prevSettingData ? prevSettingData.amount_override : null,
    buyer_at: prevSettingData ? prevSettingData.buyer_at : null,
    buyer_before: prevSettingData ? prevSettingData.buyer_before : null,
    by_others: prevSettingData ? prevSettingData.by_others : null,
    code: prevSettingData ? prevSettingData.code : null,
    letter: prevSettingData ? prevSettingData.letter : null,
    number: prevSettingData ? prevSettingData.number : null,
    payees: prevSettingData?.payees ?? [],
    payers: prevSettingData?.payers ?? [],
    seller_at: prevSettingData ? prevSettingData.seller_at : null,
    seller_before: prevSettingData ? prevSettingData.seller_before : null,
    kind: "by-letter-number"
  };

  if (prevSettingData) {
    const { letter, number, code } = prevSettingData;
    if (!letter && !number && code) {
      formData.kind = "code";
    }
  }

  return formData;
};

type Params = {
  prevSettingData?: ChargeSetting,
  onCreate: (data: GQLCreateChargeSettingPayload) => void,
  onUpdate: (data: GQLUpdateChargeSettingPayload) => void,
}

export const useCreateSettingModal = ({
  prevSettingData,
  onCreate,
  onUpdate
}: Params) => {
  const settingForm = useForm<SettingForm>({ defaultValues: createSettingInitialValues(prevSettingData),
    mode: 'onChange' });

  const payeesFieldArray = useFieldArray({ control: settingForm.control, name: 'payees' });
  const payersFieldArray = useFieldArray({ control: settingForm.control, name: 'payers' });

  const settingName = prevSettingData ? getSettingName(prevSettingData) : "";
  const isDefaultForAllSetting = settingName === defaultForAll;

  const { data: listingsResponse } = useGetListings({ code: "allowed_order_associations" });
  const { data: listingsResponseLetters } = useGetListings({ code: "allowed_ledger_letters" });

  const allowedListings = listingsResponse?.getListing.entries || [];
  const allowedLetters = listingsResponseLetters?.getListing.entries || [];

  const onConfirm = settingForm.handleSubmit((data) => {
    const { kind, ...rest } = data;

    const percents = {
      buyer_at: rest.buyer_at ?? 0,
      buyer_before: rest.buyer_before ?? 0,
      by_others: rest.by_others ?? 0,
      seller_at: rest.seller_at ?? 0,
      seller_before: rest.seller_before ?? 0
    };

    if (prevSettingData) {
      onUpdate({
        settingId: prevSettingData._id,
        data: {
          ...rest,
          ...percents,
          payees: rest.payees ?? [],
          payers: rest.payers ?? []
        }
      });
    } else {
      onCreate({
        ...rest,
        ...percents,
        payees: rest.payees ?? [],
        payers: rest.payers ?? []
      });
    }
  });

  return {
    allowedListings,
    allowedLetters,
    settingForm,
    payeesFieldArray,
    payersFieldArray,
    onConfirm,
    isDefaultForAllSetting
  };
};
